import type {SlotGameStore, BaseComponentConfig} from "@gamzix/slot_core";

import {GameDataStore} from "../../../src/GameDataStore";

class FreeSpinsGameComponent extends SlotGame.src.component.FreeSpinsGameComponent {
    static events = {
        START_INTRO_TRANSITION: "FreeSpinsGameComponent.START_INTRO_TRANSITION",
        PLAY_SOUND_END: "FreeSpinsGameComponent.PLAY_SOUND_END",
        END_INTRO_TRANSITION: "FreeSpinsGameComponent.END_INTRO_TRANSITION",
        START_OUTRO_TRANSITION: "FreeSpinsGameComponent.START_OUTRO_TRANSITION",
        END_OUTRO_TRANSITION: "FreeSpinsGameComponent.END_OUTRO_TRANSITION",
        START_LEVEL_1: "FreeSpinsGameComponent.START_LEVEL_1",
        START_LEVEL_2: "FreeSpinsGameComponent.START_LEVEL_2",
        START_LEVEL_3: "FreeSpinsGameComponent.START_LEVEL_3",
        OUTRO_POPUP_START_COUNT: "FreeSpinsGameComponent.OUTRO_POPUP_START_COUNT",
        OUTRO_POPUP_END_COUNT: "FreeSpinsGameComponent.OUTRO_POPUP_END_COUNT"
    }

    private popupContainer;
    private levelContainer;
    private congratsContainer;
    private outroPopupContainer;
    private isOutroClicked = false;
    private timeIntroTransitionMainSound = 9000;
    private timeOutroTransitionMainSound = 8000;
    private timePlayCounterAnimation = 3000;

    constructor(config: BaseComponentConfig) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),

            isEnglishLang: () => (<SlotGameStore>game.store).language === "en",
            isWin1level: () => this.gameData.freespinLevel == 1,
            isWin2level: () => this.gameData.freespinLevel == 2,
            isWin3level: () => this.gameData.freespinLevel == 3,
        };

        this.text_checkers = {
            ...super.getTextCheckers(),

            GET_TOTAL_WIN_NUMBER: () => this.store.toFormatMoney(this.store.gameData.winAmount),
        };
    }

    onGameReady() {
        super.onGameReady();

        this.popupContainer = game.tree.findByName('freeSpinsGame.wrapper_container.intro_popup_container');
        this.levelContainer = game.tree.findByName('freeSpinsGame.wrapper_container.intro_popup_container.content.level_container');
        this.congratsContainer = game.tree.findByName("freeSpinsGame.wrapper_container.intro_popup_container.content.congrat_container");
        this.outroPopupContainer = game.tree.findByName('freeSpinsGame.wrapper_container.intro_popup_container.content.outro_popup_container');
    }

    async showStartPopup() {
        this.onChangeOrientation();
        game.emit(FreeSpinsGameComponent.events[`START_LEVEL_${this.gameData.freespinLevel}`]);

        this.gameData.isFreespinGamePopupsInProgress = true;

        await game.utils.GameUtils.playAlphaAnimation(this.levelContainer, {from: 0, to: 1, time: 500, timeWait: 0});
        await game.utils.GameUtils.playAlphaAnimation(this.popupContainer, {from: 0, to: 1, time: 500, timeWait: 0});
        
        await Promise.race([
            game.onceDataPromise('Button.clicked', {
                component: 'freeSpinsGame',
                name: 'freeSpinsGame.wrapper_container.2nd_transition_screen'
            }),
            this.waitClick(),
            game.utils.delay(this.timeIntroTransitionMainSound)
        ]);

        this.store.gameData.freeSpinsGame.freeSpinGameRequest();
        game.emit('UI.UPDATED');

        await this.hideIntroPopup();
    }

    async showFinishPopup() {
        await this.showOutroPopup();
        if (this.isOutroClicked) {
            await Promise.race([
                game.onceDataPromise('Button.clicked', {
                    component: 'freeSpinsGame',
                    name: 'freeSpinsGame.wrapper_container.2nd_transition_screen'
                }),
                game.utils.delay(2000),
                this.waitClick()
            ]);
        } else {
            await Promise.race([
                this.waitClick(),
                game.onceDataPromise('Button.clicked', {
                    component: 'freeSpinsGame',
                    name: 'freeSpinsGame.wrapper_container.2nd_transition_screen'
                }),
                game.utils.delay(this.timeOutroTransitionMainSound - this.timePlayCounterAnimation)
            ]);
        }

        this.isOutroClicked = false;

        await this.hideOutroPopup();
    }

    async showIntroPopup() {
        game.emit(FreeSpinsGameComponent.events[`START_LEVEL_${this.gameData.freespinLevel}`]);

        this.gameData.isFreespinGamePopupsInProgress = true;

        await game.utils.GameUtils.playAlphaAnimation(this.levelContainer, {from: 0, to: 1, time: 500, timeWait: 0});
        await game.utils.GameUtils.playAlphaAnimation(this.popupContainer, {from: 0, to: 1, time: 500, timeWait: 0});
    }
    
    async hideIntroPopup() {
        game.emit(FreeSpinsGameComponent.events.END_INTRO_TRANSITION, {});

        game.emit(FreeSpinsGameComponent.events.PLAY_SOUND_END);
        await game.utils.GameUtils.playAlphaAnimation(this.popupContainer, {from: 1, to: 0, time: 500, timeWait: 0});
        await game.utils.GameUtils.playAlphaAnimation(this.levelContainer, {from: 1, to: 0, time: 0, timeWait: 0});
        await game.utils.GameUtils.playAlphaAnimation(this.congratsContainer, {from: 1, to: 0, time: 0, timeWait: 0});

    }

    async showOutroPopup() {
        const text = game.tree.findByName('freeSpinsGame.wrapper_container.intro_popup_container.content.outro_popup_container.totalWinFont');

        text.options.text = this.store.toFormatMoney(0);
        this.onChangeOrientation();

        game.emit(FreeSpinsGameComponent.events.START_OUTRO_TRANSITION);

        
        await game.utils.GameUtils.playAlphaAnimation(this.outroPopupContainer, {from: 0, to: 1, time: 300, timeWait: 0});
        await game.utils.GameUtils.playAlphaAnimation(this.popupContainer, {from: 0, to: 1, time: 500, timeWait: 0});
        
        game.emit(FreeSpinsGameComponent.events.OUTRO_POPUP_START_COUNT);

        await Promise.race([
            this.waitClick(),
            game.onceDataPromise('Button.clicked', {
                component: 'freeSpinsGame',
                name: 'freeSpinsGame.wrapper_container.2nd_transition_screen'
            }).then(() => this.isOutroClicked = true),
            game.utils.GameUtils.playCounterAnimation(text, {
                from: 0,
                to: this.store.gameData.winAmount,
                time: this.timePlayCounterAnimation,
                timeWait: 0
            })
        ]);

        game.utils.removeCall(text._tween);
        
        game.emit(FreeSpinsGameComponent.events.OUTRO_POPUP_END_COUNT);

        text.options.text = this.store.toFormatMoney(this.store.gameData.winAmount);
        this.onChangeOrientation();
    }

    async hideOutroPopup() {

        game.emit(FreeSpinsGameComponent.events.END_OUTRO_TRANSITION, {});

        this.gameData.isFreespinGamePopupsInProgress = false

        game.emit(FreeSpinsGameComponent.events.PLAY_SOUND_END);
        await game.utils.GameUtils.playAlphaAnimation(this.popupContainer, {from: 1, to: 0, time: 300, timeWait: 0});
        await game.utils.GameUtils.playAlphaAnimation(this.outroPopupContainer, {from: 1, to: 0, time: 300, timeWait: 0});
    }

    get store(): SlotGameStore {
        return (<SlotGameStore>game.store)
    }

    get gameData(): GameDataStore {
        return (<GameDataStore>this.store.gameData);
    }
}

export {FreeSpinsGameComponent};

//@ts-ignore
Game.src.component.FreeSpinsGameComponent = FreeSpinsGameComponent;